package fr.frux.feature.map

import androidx.compose.runtime.Composable
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.icons.hybridMarkerTint
import fr.frux.core.designsystem.theme.FruxExtendedColors
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapMarker
import fr.frux.core.maps.MarkerBadge
import fr.frux.core.maps.MarkerId
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_marker_label_needs_check
import fr.frux.feature.map.resources.map_site_tree_locked_label
import fr.frux.shared.domain.model.ClusterPoint
import fr.frux.shared.domain.model.HarvestPart
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.TreeStatus
import fr.frux.shared.domain.model.needsCheck
import fr.frux.shared.presentation.resolveTreeStatus
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Instant

/**
 * Ce qu'il faut savoir d'un arbre pour dessiner son marqueur (#622). Un arbre du lot detaille et
 * le meme arbre isole dans la pyramide passent tous deux par ici : leurs marqueurs ne peuvent donc
 * pas diverger au passage d'un zoom a l'autre.
 */
internal data class TreeMarkerFacts(
    val id: Long,
    val latitude: Double,
    val longitude: Double,
    val scientificName: String,
    val commonNameFr: String,
    val harvestPart: HarvestPart?,
    val status: TreeStatus,
    val lastObservedAt: Instant?,
    val needsCheck: Boolean,
    val locked: Boolean,
)

// La partie vient du serveur, qui l'a deja resolue au mois courant.
internal fun Tree.markerFacts(lockedSiteIds: Set<Long>): TreeMarkerFacts =
    TreeMarkerFacts(
        id = id,
        latitude = latitude,
        longitude = longitude,
        scientificName = species.scientificName,
        commonNameFr = species.commonNameFr,
        harvestPart = harvestPart,
        status = status,
        lastObservedAt = lastObservedAt,
        needsCheck = needsCheck,
        // Un arbre d'un lieu prive ferme porte le cadenas du lieu au zoom de detail.
        locked = siteId?.let { it in lockedSiteIds } == true,
    )

/**
 * La pyramide ne porte pas la partie recoltee : elle se resout ici au mois courant, par la regle
 * du serveur. Une espece absente du referentiel local ne masque pas l'arbre, que l'amas parent
 * compte deja : il se dessine sans glyphe ni nom.
 */
internal fun ClusterPoint.markerFacts(
    species: Species?,
    currentMonth: Int,
): TreeMarkerFacts? {
    val isolated = tree ?: return null
    return TreeMarkerFacts(
        id = isolated.id,
        latitude = latitude,
        longitude = longitude,
        scientificName = species?.scientificName.orEmpty(),
        commonNameFr = species?.commonNameFr.orEmpty(),
        harvestPart = species?.harvests?.mapHarvestPart(currentMonth),
        status = isolated.status,
        lastObservedAt = isolated.lastObservedAt,
        needsCheck = isolated.needsCheck,
        locked = isolated.locked,
    )
}

/** Meme regle que `HarvestPartResolver` du backend : la fenetre en saison, sinon la premiere. */
internal fun List<HarvestWindow>.mapHarvestPart(month: Int): HarvestPart? =
    (firstOrNull { it.isInSeason(month) } ?: firstOrNull())?.part

/**
 * Le libelle reste hors de [toMapMarker] pour que la traduction se teste sans harnais Compose. Il
 * porte l'etat, pas seulement le nom (#265) : le contour crenele est muet pour un lecteur d'ecran.
 */
@Composable
internal fun TreeMarkerFacts.markerLabel(): String =
    when {
        locked -> stringResource(Res.string.map_site_tree_locked_label, commonNameFr)
        needsCheck -> stringResource(Res.string.map_marker_label_needs_check, commonNameFr)
        else -> commonNameFr
    }

/**
 * La couleur est decidee ici : `core-maps` ne connait pas le vocabulaire de statut de Frux.
 */
internal fun TreeMarkerFacts.toMapMarker(
    colors: FruxExtendedColors,
    harvests: List<HarvestWindow>,
    currentMonth: Int,
    now: Instant,
    label: String,
    selected: Boolean,
): MapMarker {
    val resolved = resolveTreeStatus(status, harvests, currentMonth, lastObservedAt, now)
    val family = speciesFamilyColor(scientificName, harvestPart, colors)
    val glyph = speciesGlyphKey(scientificName, harvestPart)
    return MapMarker(
        id = MarkerId(id),
        position = Location(latitude, longitude),
        // Hybride famille x phase (#204) : cf. doc de `hybridMarkerTint`.
        tint = if (locked) colors.siteLocked else hybridMarkerTint(family, resolved, colors),
        soon = resolved.markerSoon(),
        alpha = status.markerAlpha(),
        label = label,
        selected = selected,
        // Même contour crénelé pour TO_CONFIRM et contesté par les pairs (ADR 0015, "même
        // vocabulaire visuel") : la carte ne distingue jamais pourquoi, seulement que l'arbre
        // demande une vérification.
        flagged = needsCheck,
        iconKey = if (locked) FruxSpeciesGlyphs.KEY_CADENAS else glyph,
        // Satellite du regroupement (#710) : l'espece, a sa couleur de famille, jamais au statut
        // ni au cadenas du lieu. La couronne dit ce qui pousse la, pas dans quel etat.
        badge = glyph?.let { MarkerBadge(iconKey = it, tint = family) },
    )
}
